
  import {
    defineComponent,
    ref,
    watch,
    computed,
    onBeforeUnmount,
    provide,
  } from 'vue';
  import { Box } from '@/ui/components/Box';
  import { Button } from '@/ui/components/Button';
  import { Dropdown } from '@/ui/components/controls/Dropdown';
  import {
    WidgetDisplayOptions,
    MetricsType,
    AllAttributesType,
    LineChartTimeGroupingType,
    allAttrsEnum,
    NullableLineChartTimeGroupingType,
  } from '@/services/api/resources/analytics-builder/constants';
  import {
    WidgetRequestInput,
    useWidgets,
    useMetrics,
    useWidgetRequest,
    useAttributes,
    useWidgetValidation,
    useDisplay,
  } from '@/ui/concerns/analytics/widget';
  import WidgetEmpty from '@/ui/components/analytics/WidgetEmpty.vue';
  import WidgetNoData from '@/ui/components/analytics/WidgetNoData.vue';
  import InvalidWidgetWrapper from '@/ui/components/analytics/widget/InvalidWidgetWrapper.vue';
  import WidgetNoFormItem from '@/ui/components/analytics/WidgetNoFormItem.vue';
  import LineChart from '@/ui/components/analytics/displays/charts/LineChart.vue';
  import BarChart from '@/ui/components/analytics/displays/charts/BarChart.vue';
  import PieChart from '@/ui/components/analytics/displays/charts/PieChart.vue';
  import { ReportFilter, ReportWidget } from '@/services/api/resources/analytics-builder/model';
  import SingleNumber from '@/ui/components/analytics/displays/SingleNumber.vue';
  import Table from '@/ui/components/analytics/displays/Table.vue';
  import { Modal } from '@/ui/components/Modal';
  import DetailedView from '@/ui/pages/analytics/Builder/DetailedView.vue';
  import { Loader } from '@/ui/components/Loader';
  import { formItemDetailsFromWidgetKey } from '@/ui/injectionKeys';
  import { isEmpty } from 'lodash/fp';
  import { useFilterConfiguration } from '@/ui/concerns/analytics/useFilters';
  import { offlineFormatsWhiteList, SupportedExportFormat } from '@/services/api/resources/analytics';
  import { useExports } from '@/ui/concerns/analytics/exports/useExports';
  import DropdownActionMenu from '@/ui/components/analytics/widget/DropdownActionMenu.vue';
  import FilterButton from '@/ui/components/analytics/FilterButton.vue';
  import emitter from '@/services/emitter';
  import { useAnalyticsMap } from '@/ui/concerns/analytics/useAnalyticsMap';
  import WidgetTitle from '@/ui/components/analytics/widget/WidgetTitle.vue';
  import { useReportInjection } from '@/ui/concerns/analytics/useReportInjection';
  import { ResponsesTypes } from '@/entities/checklist';

  interface WidgetProps {
    widget: ReportWidget;
  }

  export default defineComponent<WidgetProps>({
    components: {
      WidgetTitle,
      Table,
      SingleNumber,
      LineChart,
      BarChart,
      PieChart,
      WidgetNoData,
      WidgetNoFormItem,
      WidgetEmpty,
      InvalidWidgetWrapper,
      Button,
      Box,
      Dropdown,
      Modal,
      DetailedView,
      Loader,
      DropdownActionMenu,
      FilterButton,
    },
    props: ['widget'],
    setup({ widget }, { emit }) {
      const { isGuest, reportId: id, report } = useReportInjection();
      const { isWidgetExportLoading, exportWidget } = useExports();
      const {
        combineFilters,
        getPage,
        getPerPage,
        shouldTogglePercentageOnAttributeChange,
        shouldChangeTimeGroupingOnAttributeChange,
      } = useWidgets();
      const { getMetricsWithLabels, findMetricLabel } = useMetrics();
      const {
        getAttributesByMetricWithLabels,
        getAttributesByMetric,
        getDisplayByResponseType,
      } = useAttributes();
      const { isRequestParamsValid, getDisplayByFormItem } = useWidgetValidation();
      const { displayOptions, getSelectedDisplayOption, shouldDisableDisplay } = useDisplay();
      const { isMetricOnlyRatioValues, getDefaultDisplay } = useAnalyticsMap();

      const isDetailedViewModalOpen = ref(false);
      const suspendLoaderTask = ref(false);

      const selectedMetric = ref<MetricsType | null>(widget.metric);
      const selectedAttribute = ref<AllAttributesType | null>(widget.attribute);
      const selectedDisplay = ref<WidgetDisplayOptions | null>(getSelectedDisplayOption(widget));
      const selectedLineChartTimeGrouping = ref<NullableLineChartTimeGroupingType>(widget.lineChartTimeGrouping);
      const percentageToggled = ref<boolean>(widget.percentage);
      const page = ref(getPage(1, selectedDisplay.value));
      const perPage = ref(getPerPage(selectedDisplay.value));
      const combinedFilters = computed<ReportFilter>(() => combineFilters(widget.filters, report.value!.filters));
      const requestParams = ref<WidgetRequestInput>({
        metric: selectedMetric.value,
        attribute: selectedAttribute.value,
        display: selectedDisplay.value,
        percentageToggled: percentageToggled.value,
        filters: combinedFilters.value,
        lineChartTimeGrouping: selectedLineChartTimeGrouping.value,
        page: page.value,
        perPage: perPage.value,
      });

      const requestParamsProxyHandler = {
        set: (target: WidgetRequestInput, prop: keyof WidgetRequestInput, value: never) => {
          if (requestParams.value[prop] !== value) {
            target[prop] = value;
          }
          return true;
        },
      };

      const disablePercentageButton = computed(
        () => isMetricOnlyRatioValues(selectedMetric.value) || selectedAttribute.value === allAttrsEnum.total,
      );

      const formItemId = computed(() => combinedFilters.value.formItem ?? undefined);

      const getFormItemDetailsCallback = (params: Partial<WidgetRequestInput> = {}) => {
        if (selectedAttribute.value !== allAttrsEnum.response) {
          if (!isEmpty(params)) {
            updateRequestParams(params);
          }
          return;
        }

        const display = getDisplayByResponseType(formItemDetails.value?.type as ResponsesTypes);
        if (display) {
          updateDisplay(display);
        }

        if (selectedDisplay.value !== requestParams.value.display) {
          params.display = selectedDisplay.value;
        }

        if (!isEmpty(params)) {
          updateRequestParamsWithPageReset(params);
        }
      };

      const {
        getWidgetDataTask,
        data: chartData,
        getFormItemDetailsTask,
        formItemDetails,
      } = useWidgetRequest(isGuest ? id?.value : null);

      const resetPagination = () => {
        page.value = getPage(1, selectedDisplay.value);
        perPage.value = getPerPage(selectedDisplay.value);
      };

      const updateRequestParams = (params: Partial<WidgetRequestInput>) => {
        requestParams.value = {
          ...requestParams.value,
          ...params,
        };
      };

      const updateRequestParamsWithPageReset = (params: Partial<WidgetRequestInput>) => {
        resetPagination();
        updateRequestParams({ ...params, page: page.value, perPage: perPage.value });
      };

      const hasNoSelection = computed(() =>
        !(selectedMetric.value && selectedAttribute.value && selectedDisplay.value));

      const noData = computed(() => {
        if (selectedDisplay.value === WidgetDisplayOptions.singleNumber) {
          return chartData.value === undefined;
        }

        if (selectedDisplay.value === WidgetDisplayOptions.table) {
          return isEmpty(chartData.value) || isEmpty(chartData.value.items);
        }

        return isEmpty(chartData.value) || isEmpty(chartData.value.series);
      });

      const showNoFormItemDisplay = computed(() => {
        if (selectedAttribute.value !== allAttrsEnum.response) {
          return false;
        }

        return !combinedFilters.value.formItem;
      });

      const isTableDisplayVisible = computed(() => !hasNoSelection.value && !noData.value
        && !showNoFormItemDisplay.value && selectedDisplay.value === WidgetDisplayOptions.table);

      const isPieChartDisplayVisible = computed(() => !hasNoSelection.value && !noData.value
        && selectedDisplay.value === WidgetDisplayOptions.pieChart);

      const singleNumberText = computed(() => findMetricLabel(selectedMetric.value as MetricsType));

      const reportFiltersCount = computed(() => useFilterConfiguration().countFilters(widget.filters));
      const isResponseAttributeSelected = computed(() => selectedAttribute.value === allAttrsEnum.response);
      const disableExport = computed(() => !widget.id || hasNoSelection.value || isWidgetExportLoading.value);
      const hideChartOnlyActions = computed(() => hasNoSelection.value
        || isWidgetExportLoading.value
        || ![
          WidgetDisplayOptions.lineChart,
          WidgetDisplayOptions.barChart,
          WidgetDisplayOptions.pieChart,
        ].includes(selectedDisplay.value!));

      const updateLineChartTimeGrouping = (value: LineChartTimeGroupingType) => {
        selectedLineChartTimeGrouping.value = value;
        emitter.emit('analyticsLineChartTimeGroupingChanged', { key: widget.key, value });
      };

      const updateDisplay = (value: WidgetDisplayOptions | null) => {
        selectedDisplay.value = value;
        emit('display-change', widget.key, value);
      };

      const togglePercentage = () => {
        percentageToggled.value = !percentageToggled.value;
        emit('percentage-toggled', widget.key);
      };

      const onDisplayChange = (value: string | null) => {
        updateDisplay(value as WidgetDisplayOptions | null);

        if (requestParams.value.display !== value) {
          updateRequestParamsWithPageReset({ display: value as WidgetDisplayOptions });
        }
      };

      const onTogglePercentage = () => {
        togglePercentage();
        if (requestParams.value.percentageToggled !== percentageToggled.value) {
          updateRequestParams({ percentageToggled: percentageToggled.value });
        }
      };

      const onAttributeChange = (value: AllAttributesType | null) => {
        selectedAttribute.value = value;

        let shouldFetchFormItemDetails = false;
        const params : Partial<WidgetRequestInput> = {};
        const proxy = new Proxy(params, requestParamsProxyHandler);

        proxy.attribute = value;

        if (value === allAttrsEnum.response && combinedFilters.value.formItem) {
          if (combinedFilters.value.formItem === formItemDetails.value?.id) {
            updateDisplay(getDisplayByFormItem(formItemDetails.value));
            proxy.display = selectedDisplay.value;
          } else {
            shouldFetchFormItemDetails = true;
          }
        }

        const display = getDefaultDisplay(selectedMetric.value, value) ?? selectedDisplay.value;
        if (display !== selectedDisplay.value) {
          updateDisplay(display);
          proxy.display = display;
        }

        if (shouldTogglePercentageOnAttributeChange(value, percentageToggled.value)) {
          togglePercentage();
          proxy.percentageToggled = percentageToggled.value;
        }

        if (shouldChangeTimeGroupingOnAttributeChange(value, selectedLineChartTimeGrouping.value)) {
          updateLineChartTimeGrouping('quarter');
          proxy.lineChartTimeGrouping = 'quarter';
        }

        if (!isEmpty(params)) {
          updateRequestParamsWithPageReset(params);
        }

        if (shouldFetchFormItemDetails) {
          getFormItemDetailsTask.value.run({
            id: formItemId.value,
            cb: () => {
              updateDisplay(getDisplayByFormItem(formItemDetails.value));
              updateRequestParams({ display: selectedDisplay.value });
            },
          });
        }

        emit('attribute-changed', widget.key, value);
      };

      const onAddCardAfter = () => {
        emit('add-card-after', widget.key);
      };

      const onDeleteCard = () => {
        emit('delete-card', widget.key);
      };

      const onFilterOpen = () => {
        emit('widget-filter-open', widget.key);
      };

      const onDetailedViewOpen = () => {
        isDetailedViewModalOpen.value = true;
      };

      const onDetailedViewClose = () => {
        isDetailedViewModalOpen.value = false;
      };

      const onPageChanged = (value: number) => {
        page.value = value;
        updateRequestParams({ page: value });
      };

      const onInvalidStateShown = () => {
        suspendLoaderTask.value = true;
      };

      const onTaskTriggered = () => {
        suspendLoaderTask.value = true;
      };

      const onExport = (format: SupportedExportFormat) => {
        if (offlineFormatsWhiteList.includes(format)) {
          emitter.emit('analyticsExportWidget', { key: widget.key, format });

          return;
        }

        if (widget.id) {
          exportWidget(widget.id, format).run();
        }
      };

      const onChartFullScreen = () => emitter.emit('analyticsChartFullScreen', { key: widget.key });
      const onChartPrint = () => emitter.emit('analyticsChartPrint', { key: widget.key });

      const metrics = getMetricsWithLabels();
      const attributes = computed(() => getAttributesByMetricWithLabels(selectedMetric.value));

      const disableDisplayOption = (option: WidgetDisplayOptions) => shouldDisableDisplay(
        option,
        selectedMetric,
        selectedAttribute,
        formItemDetails,
      );

      watch(() => combinedFilters.value, (newFilters, oldFilters) => {
        if (oldFilters.formItem !== newFilters.formItem && isResponseAttributeSelected.value) {
          getFormItemDetailsTask.value.run({
            id: formItemId.value,
            cb: () => getFormItemDetailsCallback({ filters: newFilters }),
          });
          return;
        }

        updateRequestParams({ filters: newFilters });
      }, { deep: true });

      watch(selectedMetric, (value: MetricsType | null) => {
        const params : Partial<WidgetRequestInput> = {};
        const proxy = new Proxy(params, requestParamsProxyHandler);

        proxy.metric = value;

        if (value) {
          if (!getAttributesByMetric(value).includes(selectedAttribute.value)) {
            selectedAttribute.value = null;
            emit('attribute-changed', widget.key, null);
            proxy.attribute = null;

            updateDisplay(null);
            proxy.display = null;
          }

          if (isMetricOnlyRatioValues(value) && !percentageToggled.value) {
            togglePercentage();
            proxy.percentageToggled = percentageToggled.value;
          }
        }

        if (!isEmpty(params)) {
          updateRequestParamsWithPageReset(params);
        }

        emit('metric-changed', widget.key, value);
      });

      watch(() => widget.lineChartTimeGrouping, value => {
        selectedLineChartTimeGrouping.value = value;
        if (requestParams.value.lineChartTimeGrouping !== value) {
          updateRequestParams({ lineChartTimeGrouping: value });
        }
      });

      watch(() => requestParams.value, () => {
        if (isRequestParamsValid(requestParams.value, formItemDetails.value)) {
          getWidgetDataTask.value.run(requestParams.value);
        }
      }, { deep: true });

      provide(formItemDetailsFromWidgetKey, formItemDetails);

      onBeforeUnmount(() => {
        document.querySelectorAll('.chartist-tooltip')
          .forEach(el => el.classList.remove('tooltip-show'));
      });

      return {
        isGuest,
        WidgetDisplayOptions,
        selectedMetric,
        selectedAttribute,
        selectedDisplay,
        selectedLineChartTimeGrouping,
        percentageToggled,
        isDetailedViewModalOpen,
        onAttributeChange,
        onDisplayChange,
        onTogglePercentage,
        onAddCardAfter,
        onDeleteCard,
        onFilterOpen,
        onDetailedViewOpen,
        onDetailedViewClose,
        displayOptions,
        metrics,
        attributes,
        chartData,
        getWidgetDataTask,
        getFormItemDetailsTask,
        taskParams: { id: formItemId.value, cb: getFormItemDetailsCallback },
        disablePercentageButton,
        hasNoSelection,
        noData,
        showNoFormItemDisplay,
        isTableDisplayVisible,
        isPieChartDisplayVisible,
        disableDisplayOption,
        singleNumberText,
        page,
        perPage,
        onPageChanged,
        onExport,
        onChartFullScreen,
        onChartPrint,
        isWidgetExportLoading,
        disableExport,
        hideChartOnlyActions,
        requestParams,
        suspendLoaderTask,
        onInvalidStateShown,
        onTaskTriggered,
        reportFiltersCount,
      };
    },
  });
